import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NumbersIcon from '@mui/icons-material/Numbers';
import { FUNCTION_CATEGORIES, getFunctionsByCategory } from './functionRegistry';
import type { FunctionCategory } from './types';

const CATEGORY_ICONS: Record<FunctionCategory, React.ReactNode> = {
  text: <TextFieldsIcon fontSize="small" />,
  logic: <FilterAltIcon fontSize="small" />,
  date: <CalendarMonthIcon fontSize="small" />,
  numeric: <NumbersIcon fontSize="small" />,
};

interface AddStepMenuProps {
  onAdd: (functionName: string) => void;
  compact?: boolean;
}

const AddStepMenu: React.FC<AddStepMenuProps> = ({ onAdd, compact }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (functionName: string) => {
    onAdd(functionName);
    handleClose();
  };

  return (
    <>
      {compact ? (
        <Tooltip title="Add transformation step">
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              width: 28,
              height: 28,
              '&:hover': { bgcolor: 'primary.main' },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{
            textTransform: 'none',
            fontSize: '0.78rem',
            borderStyle: 'dashed',
          }}
        >
          Add Step
        </Button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { minWidth: 240, maxHeight: 360, overflow: 'auto' },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle2" color="text.primary">
            Add Transformation
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose a function to add to the pipeline
          </Typography>
        </Box>

        {FUNCTION_CATEGORIES.map((cat, catIdx) => {
          const funcs = getFunctionsByCategory(cat.key);
          if (funcs.length === 0) return null;
          return (
            <Box key={cat.key}>
              {catIdx > 0 && <Divider />}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ px: 2, pt: 1, pb: 0.25 }}
              >
                {CATEGORY_ICONS[cat.key]}
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  textTransform="uppercase"
                  letterSpacing={0.5}
                >
                  {cat.label}
                </Typography>
              </Stack>
              <MenuList dense sx={{ py: 0 }}>
                {funcs.map((func) => (
                  <MenuItem
                    key={func.name}
                    onClick={() => handleSelect(func.name)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}
                        >
                          {func.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {func.description}
                        </Typography>
                      }
                    />
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
          );
        })}
      </Popover>
    </>
  );
};

export default AddStepMenu;
